// src/components/intake/IntakePage.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../ui";
import Loader from "../Loader";
import {
  getIntake,
  saveIntakePage,
  skipIntakePage,
} from "../../shared/_services/intake.services";
import { LOAD_FAILED, TRY_AGAIN } from "../onboarding/onboardingCopy";
import { isWrongStage, useStageNavigation } from "../onboarding/onboardingNav";
import { ActionBar, IntakeLayout, QuestionSteps } from "./IntakeLayout";
import { BODIES } from "./bodies";
import {
  bodyOf,
  draftFrom,
  isAnswered,
  lineErrorsOf,
  statusKeyOf,
} from "./intakeRules";
import {
  DASHBOARD_PATH,
  YOURE_IN_PATH,
  intakePath,
  saveFailure,
} from "./intakeNav";
import {
  ANSWERS_LOAD_FAILED,
  BACK_TO_YOURE_IN,
  CHOOSE_YOUR_PLAN,
  CONTINUE_LATER,
  NAV_LABELS,
  PAGE_LEDES,
  PAGE_TITLES,
  SKIP_BODY,
  SKIP_CANCEL,
  SKIP_CONFIRM,
  SKIP_TITLE,
  STATUS_NEED,
  STATUS_OR,
  STATUS_SKIP,
} from "./intakeCopy";

const ID = "intake";

// One question page, /intake/:pageKey (spec § Screens, § The seven pages).
// It reads GET /onboarding/intake on entry and prefills from it; Next saves
// the page and goes to the following page in the served order; Skip (from the
// status line, behind a dialog) marks it complete without an answer; Continue
// later saves an answered page and goes to the dashboard; Back never writes.
// A 503 keeps every entry and says it couldn't save (no queue). With the vault
// unavailable the page says so and renders no form, so an empty form can
// never overwrite an answer (decision 12).
// Lucide chevron-left / arrow-right, as the prototype's action bar has them.
const Icon = ({ d }) => (
  <svg
    viewBox="0 0 24 24"
    width="1rem"
    height="1rem"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="tw-shrink-0"
  >
    <path d={d} />
  </svg>
);

export default function IntakePage({
  pageKey,
  focusPart,
  plaidSeen,
  onPlaidChange,
}) {
  const navigate = useNavigate();
  const go = useStageNavigation();
  const [load, setLoad] = useState({ status: "loading", data: null });
  const [draft, setDraft] = useState(null);
  const [lineErrors, setLineErrors] = useState({});
  const [notice, setNotice] = useState(null);
  const [sending, setSending] = useState(false);
  const [skipOpen, setSkipOpen] = useState(false);
  const h1 = useRef(null);
  const status = useRef(null);
  // False once the page has gone: a save or skip that lands after Back must
  // not navigate on (StrictMode-safe: the effect re-arms it).
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const read = useCallback(async () => {
    setLoad({ status: "loading", data: null });
    try {
      const res = await getIntake();
      const page = res.data.pages.find((p) => p.key === pageKey);
      if (page && res.data.vault === "ok")
        setDraft(draftFrom(page, res.data.answers?.[pageKey]));
      setLoad({ status: "ready", data: res.data });
    } catch (_) {
      setLoad({ status: "error", data: null });
    }
  }, [pageKey]);

  useEffect(() => {
    read();
  }, [read]);

  const ready = load.status === "ready";
  useEffect(() => {
    if (ready && !focusPart) h1.current?.focus();
  }, [ready, focusPart]);

  if (load.status === "loading")
    return (
      <div className="stars-dark tw-min-h-screen tw-bg-bg tw-text-text">
        <Loader />
      </div>
    );
  if (load.status === "error")
    return (
      <div className="stars-dark tw-min-h-screen tw-bg-bg tw-text-text">
        <div role="alert" className="tw-p-8">
          <span>{LOAD_FAILED}</span>{" "}
          <Button variant="text" onClick={read}>
            {TRY_AGAIN}
          </Button>
        </div>
      </div>
    );

  const { pages, completed, vault } = load.data;
  const index = pages.findIndex((p) => p.key === pageKey);
  if (index === -1)
    return (
      <Navigate
        to={intakePath(load.data.nextPageKey ?? pages[0].key)}
        replace
      />
    );
  const page = pages[index];
  const last = index === pages.length - 1;
  const prev = pages[index - 1];
  const unfinished = pages.find(
    (p) => p.key !== pageKey && !completed.includes(p.key),
  );
  const primaryLabel = !last
    ? NAV_LABELS[pages[index + 1].key]
    : unfinished
      ? NAV_LABELS[unfinished.key]
      : CHOOSE_YOUR_PLAN;
  const formShown = vault === "ok";
  const answered = formShown && isAnswered(page, draft);
  const Body = BODIES[page.kind];

  // Where a save or a skip goes: the following page; after the last page,
  // the first unfinished one, or routeFor (the plan seam) once the set is full.
  const onward = (res) => {
    if (!alive.current) return;
    if (!last) navigate(intakePath(pages[index + 1].key));
    else if (res.data.nextPageKey) navigate(intakePath(res.data.nextPageKey));
    else go(res.data);
  };
  const failed = (err) => {
    if (!alive.current) return;
    if (isWrongStage(err)) {
      go(err.response.data);
      return;
    }
    setNotice(saveFailure(err));
    setSending(false);
  };
  const save = async (then) => {
    const errors = lineErrorsOf(page, draft);
    setLineErrors(errors);
    if (Object.keys(errors).length) return;
    setSending(true);
    setNotice(null);
    try {
      then(await saveIntakePage(pageKey, bodyOf(page, draft)));
    } catch (err) {
      failed(err);
    }
  };
  const onPrimary = () => {
    if (sending) return;
    if (!answered) {
      status.current?.focus();
      return;
    }
    save(onward);
  };
  const onContinueLater = () => {
    if (answered)
      save(() => {
        if (alive.current) navigate(DASHBOARD_PATH);
      });
    else navigate(DASHBOARD_PATH);
  };
  const onSkip = async () => {
    setSkipOpen(false);
    setSending(true);
    setNotice(null);
    try {
      onward(await skipIntakePage(pageKey));
    } catch (err) {
      failed(err);
    }
  };

  const bar = (
    <ActionBar>
      <Button
        as={Link}
        variant="quiet"
        disabled={sending}
        to={prev ? intakePath(prev.key) : YOURE_IN_PATH}
      >
        <Icon d="M15 18l-6-6 6-6" />
        {prev ? NAV_LABELS[prev.key] : BACK_TO_YOURE_IN}
      </Button>
      {formShown && (
        <Button
          aria-disabled={answered && !sending ? undefined : "true"}
          aria-describedby={`${ID}-status`}
          loading={sending}
          onClick={onPrimary}
        >
          {primaryLabel}
          <Icon d="M5 12h14M12 5l7 7-7 7" />
        </Button>
      )}
      <p
        id={`${ID}-status`}
        ref={status}
        tabIndex={-1}
        role="status"
        aria-live="polite"
        className="tw-order-first tw-m-0 tw-min-w-0 tw-basis-full tw-text-sm tw-text-text-muted focus:tw-outline-none sm:tw-order-none sm:tw-flex-1 sm:tw-basis-60"
      >
        {notice && <span className="tw-text-danger">{notice}</span>}
        {!notice && formShown && !answered && (
          <>
            {STATUS_NEED[statusKeyOf(page)]}
            {STATUS_OR}
            <Button
              variant="text"
              disabled={sending}
              onClick={() => setSkipOpen(true)}
            >
              {STATUS_SKIP}
            </Button>
          </>
        )}
      </p>
      <div className="tw-ml-auto">
        <Button
          variant="quiet"
          size="sm"
          className="tw-min-h-11 sm:tw-min-h-9"
          loading={sending}
          onClick={onContinueLater}
        >
          {CONTINUE_LATER}
        </Button>
      </div>
    </ActionBar>
  );

  return (
    <IntakeLayout
      pages={pages}
      completed={completed}
      current={pageKey}
      bar={bar}
    >
      <QuestionSteps pages={pages} completed={completed} current={pageKey} />
      <h1
        id={`${ID}-h1`}
        ref={h1}
        tabIndex={-1}
        className="tw-m-0 tw-max-w-xl tw-text-2xl tw-font-bold focus:tw-outline-none"
      >
        {PAGE_TITLES[pageKey]}
      </h1>
      <p className="tw-mb-0 tw-mt-3 tw-max-w-2xl tw-text-md tw-text-text-muted">
        {PAGE_LEDES[pageKey]}
      </p>
      {/* The Assistant toggle's slot (decision 3): empty until the helper
          returns as a drawer with questionName, options and firstMessage. */}
      {formShown ? (
        <form
          aria-labelledby={`${ID}-h1`}
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            onPrimary();
          }}
        >
          <Body
            page={page}
            draft={draft}
            onChange={(d) => {
              setNotice(null);
              setDraft(d);
            }}
            idPrefix={ID}
            focusPart={focusPart}
            lineErrors={lineErrors}
            plaid={{
              consent: Boolean(load.data.plaid?.consent || plaidSeen?.consent),
              linked: Boolean(load.data.plaid?.linked || plaidSeen?.linked),
            }}
            onPlaidChange={onPlaidChange}
          />
        </form>
      ) : (
        <div role="alert" className="tw-mt-6 tw-text-md">
          <span>{ANSWERS_LOAD_FAILED}</span>{" "}
          <Button variant="text" onClick={read}>
            {TRY_AGAIN}
          </Button>
        </div>
      )}
      <Dialog open={skipOpen} onOpenChange={setSkipOpen}>
        <DialogContent>
          <DialogTitle>{SKIP_TITLE}</DialogTitle>
          <DialogDescription>{SKIP_BODY}</DialogDescription>
          <DialogActions>
            <DialogClose asChild>
              <Button variant="quiet">{SKIP_CANCEL}</Button>
            </DialogClose>
            <Button onClick={onSkip}>{SKIP_CONFIRM}</Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </IntakeLayout>
  );
}
